import JsonLd from "@/components/base/JsonLd";
import PageHeader from "@/components/feature/PageHeader";
import useSeo from "@/hooks/useSeo";
import SubmitForm from "@/pages/submit/components/SubmitForm";
import { site } from "@/config/site";

const ORIGIN = "https://of659.cn";

const tips = [
  { icon: "ri-file-list-3-line", title: "写清时间与地点", desc: "具体的时间节点和发生地点，是交叉核实最关键的线索。" },
  { icon: "ri-image-2-line", title: "证据优先于描述", desc: "截图、录音、合同等原始材料，比转述更有说服力。" },
  { icon: "ri-user-unfollow-line", title: "可以完全匿名", desc: "不填联系方式也能提交，只是我们无法向你反馈核实进度。" },
  { icon: "ri-attachment-line", title: "多份材料可分批补", desc: "先提交关键点，后续通过邮件继续补充，我们会持续跟进。" },
];

export default function SubmitPage() {
  useSeo({
    title: "投稿通道 - 看料爆料｜匿名提交线索、私信投稿入口",
    description:
      "通过看料爆料的投稿通道匿名提交内幕、证据或素材，填写线索表单或发送邮件即可完成投稿。平台会加密保存投稿人信息，并在 12 小时内给出初筛反馈。",
    keywords: "看料爆料投稿,看料爆料提交线索,看料爆料私信,匿名投稿入口,爆料线索提交",
    canonical: `${ORIGIN}/submit`,
  });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "看料爆料投稿通道",
    url: `${ORIGIN}/submit`,
    inLanguage: "zh-CN",
    description: "匿名提交爆料线索的投稿通道，支持表单投稿与邮件投稿。",
  };

  return (
    <>
      <JsonLd data={jsonLd} />
      <PageHeader
        crumbs={[{ label: "首页", to: "/" }, { label: "投稿通道" }]}
        eyebrow="投稿通道"
        title="把你手里的料，安全地交出来"
        desc="不需要注册，不需要真实身份。填完下面这份表单，线索就会进入我们的核实队列；你也可以直接发送邮件到投稿邮箱。"
      />

      <section className="mx-auto w-full max-w-shell px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SubmitForm />
          </div>

          <aside className="space-y-5 lg:col-span-5">
            <div className="rounded-lg border border-foreground-950/10 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">投稿前的小建议</h2>
              <ul className="mt-4 space-y-4">
                {tips.map((tip) => (
                  <li key={tip.title} className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                      <i className={`${tip.icon} text-lg`} aria-hidden="true"></i>
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">{tip.title}</span>
                      <span className="mt-0.5 block text-sm leading-relaxed text-foreground-600">{tip.desc}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-primary-800">
                <i className="ri-mail-line" aria-hidden="true"></i>
                邮件投稿
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                如果材料较多，或希望用加密方式沟通，也可以直接发送邮件。我们同样会在 12 小时内给出初筛反馈。
              </p>
              <p className="mt-3 select-all font-medium text-primary-800">{site.email}</p>
            </div>

            <div className="rounded-lg border border-foreground-950/10 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-time-line text-primary-600" aria-hidden="true"></i>
                处理时效
              </h2>
              <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
                常规线索 12 小时内完成初筛并反馈；涉及多方取证的复杂线索，核实周期通常为 2 到 7 个工作日。
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}